import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { catchError, of } from "rxjs";
import { SnackbarService } from "../../../../components/snackbar/snackbar.service";
import { TenantsApiService } from "../../../../shared/services/tenants.api.service";
import { TenantFormComponent } from "../../components/tenant-form/tenant-form.component";
import { TenantFormValue, UpdateTenantRequest } from "../../models/tenant.model";

@Component({
    selector: "app-edit-tenant",
    templateUrl: "./edit-tenant.component.html",
    styleUrl: "./edit-tenant.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TenantFormComponent]
})
export class EditTenantComponent {
    private readonly tenantsService = inject(TenantsApiService);
    private readonly router = inject(Router);
    private readonly snackbarService = inject(SnackbarService);
    private readonly tenantId = inject(ActivatedRoute).snapshot.paramMap.get("tenantId") ?? "";

    // On load failure (bad id / network) surface an error and return to the list rather
    // than throwing during change detection; the template stays on "Loading" until then.
    readonly tenant = toSignal(
        this.tenantsService.getTenant(this.tenantId).pipe(
            catchError(() => {
                this.snackbarService.show("We couldn’t load this tenant. Try again.", "error");
                void this.router.navigate(["/admin/tenants"]);
                return of(null);
            })
        ),
        { initialValue: null }
    );

    // In-flight guard so the primary can't double-submit.
    readonly submitting = signal(false);

    onSubmit(value: TenantFormValue): void {
        this.submitting.set(true);
        const request: UpdateTenantRequest = { ...value };
        this.tenantsService.updateTenant(this.tenantId, request).subscribe({
            next: () => {
                this.snackbarService.show("Tenant saved.", "success");
                void this.router.navigate(["/admin/tenants"]);
            },
            error: () => {
                this.submitting.set(false);
                this.snackbarService.show("We couldn’t save your changes. Try again.", "error");
            }
        });
    }
}
